import { useCallback, useEffect, useRef, useState } from 'react';
import { getIceServers } from '../lib/iceServers';

/**
 * Live streaming hook.
 *
 * Host mode (isHost=true):
 *   - Captures camera/mic, publishes to each viewer via WebRTC peer connections.
 *   - Receives `live_viewer_joined` events and creates offers per viewer.
 *
 * Viewer mode (isHost=false):
 *   - Subscribes to the stream, signals readiness, accepts the host's offer.
 *   - Exposes the host's remote MediaStream via `onRemoteStream` callback.
 *
 * Both:
 *   - Real-time viewer count updates (via `onViewerCount`).
 *   - Real-time comments (via `onComment`).
 *   - Real-time lifecycle events (kicked/muted/ended) (via callbacks).
 */

export default function useLiveStream({
  streamId,
  userId,
  hostId,
  isHost,
  isVideo = true,
  onRemoteStream,
  onViewerCount,
  onViewerJoined,
  onViewerLeft,
  onComment,
  onLiveGift,
  onLiveLike,
  onKicked,
  onMuted,
  onEnded,
  onError,
  onCommentBlocked,
  onMusic,
  onMusicStop,
  onBattleStarted,
  onBattleUpdate,
  onBattleEnded,
}) {
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const cameraStreamRef = useRef(null); // saved camera stream during screen share
  const peersRef = useRef(new Map()); // viewer_id -> RTCPeerConnection (host); for viewer: hostId -> pc
  const pendingIceRef = useRef(new Map()); // peer_id -> [candidate]
  const [localStream, setLocalStream] = useState(null);
  const [wsReady, setWsReady] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

  // Send via WS safely
  const wsSend = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
    }
  }, []);

  // Create a peer connection for a given remote peer
  const createPeer = useCallback(async (peerId, role) => {
    const iceServers = await getIceServers();
    const pc = new RTCPeerConnection({ iceServers });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        wsSend({
          type: 'live_ice',
          stream_id: streamId,
          to_user_id: peerId,
          candidate: e.candidate,
        });
      }
    };

    pc.ontrack = (e) => {
      if (role === 'viewer' && e.streams[0]) {
        onRemoteStream?.(e.streams[0]);
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        try { pc.restartIce(); } catch {}
      }
    };

    peersRef.current.set(peerId, pc);
    return pc;
  }, [streamId, onRemoteStream, wsSend]);

  // Apply buffered ICE candidates after remote description is set
  const drainIce = useCallback(async (peerId) => {
    const pc = peersRef.current.get(peerId);
    const queue = pendingIceRef.current.get(peerId) || [];
    if (!pc || queue.length === 0) return;
    for (const c of queue) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
    }
    pendingIceRef.current.set(peerId, []);
  }, []);

  // Host: when a new viewer joins, create offer
  const sendOfferTo = useCallback(async (viewerId) => {
    if (!localStreamRef.current) return;
    const pc = await createPeer(viewerId, 'host');
    localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsSend({
        type: 'live_offer',
        stream_id: streamId,
        to_user_id: viewerId,
        offer,
      });
    } catch (e) {
      console.error('Failed to create offer for viewer', viewerId, e);
    }
  }, [streamId, createPeer, wsSend]);

  // Viewer: on receiving host's offer, answer
  const handleOffer = useCallback(async (fromUserId, offer) => {
    const pc = await createPeer(fromUserId, 'viewer');
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      await drainIce(fromUserId);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsSend({
        type: 'live_answer',
        stream_id: streamId,
        to_user_id: fromUserId,
        answer,
      });
    } catch (e) {
      console.error('Failed to handle host offer:', e);
    }
  }, [streamId, createPeer, drainIce, wsSend]);

  // Host: on receiving viewer's answer
  const handleAnswer = useCallback(async (fromUserId, answer) => {
    const pc = peersRef.current.get(fromUserId);
    if (!pc) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
      await drainIce(fromUserId);
    } catch (e) {
      console.error('Failed to handle viewer answer:', e);
    }
  }, [drainIce]);

  // Both: handle incoming ICE candidate
  const handleIce = useCallback(async (fromUserId, candidate) => {
    const pc = peersRef.current.get(fromUserId);
    if (!pc || !pc.remoteDescription) {
      // Buffer until remote description is set
      const q = pendingIceRef.current.get(fromUserId) || [];
      q.push(candidate);
      pendingIceRef.current.set(fromUserId, q);
      return;
    }
    try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
  }, []);

  // Open WebSocket and start host or viewer flow
  useEffect(() => {
    if (!streamId || !userId) return;

    let cancelled = false;

    const setup = async () => {
      // If host, capture local media first
      if (isHost) {
        try {
          const constraints = isVideo
            ? { video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, audio: true }
            : { video: false, audio: true };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          localStreamRef.current = stream;
          setLocalStream(stream);
        } catch (e) {
          console.error('Host getUserMedia failed:', e);
          onError?.(
            e.name === 'NotAllowedError'
              ? 'Please grant camera and microphone permission'
              : 'Could not access camera'
          );
          return;
        }
      }

      // Open WebSocket
      const ws = new WebSocket(`${WS_URL}/api/ws/${userId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setWsReady(true);
        // Subscribe to this live stream (everyone, including host, so they get viewer count broadcasts)
        ws.send(JSON.stringify({ type: 'live_subscribe', stream_id: streamId }));

        // Viewer signals readiness so host knows to send an offer
        if (!isHost && hostId) {
          ws.send(JSON.stringify({
            type: 'live_viewer_ready',
            stream_id: streamId,
            to_user_id: hostId,
          }));
        }
      };

      ws.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);
          const d = msg.data || {};

          switch (msg.type) {
            case 'live_viewer_count':
              if (d.stream_id === streamId) onViewerCount?.(d.count);
              break;

            case 'live_comment':
              if (d.stream_id === streamId) onComment?.(d);
              break;

            case 'live_comment_blocked':
              if (d.stream_id === streamId) onCommentBlocked?.(d.reason);
              break;

            case 'live_music':
              if (d.stream_id === streamId) onMusic?.(d);
              break;

            case 'live_music_stop':
              if (d.stream_id === streamId) onMusicStop?.();
              break;

            case 'live_battle_started':
              if (d?.host1_stream_id === streamId || d?.host2_stream_id === streamId) {
                onBattleStarted?.(d);
              }
              break;

            case 'live_battle_update':
              if (d?.host1_stream_id === streamId || d?.host2_stream_id === streamId) {
                onBattleUpdate?.(d);
              }
              break;

            case 'live_battle_ended':
              if (d?.host1_stream_id === streamId || d?.host2_stream_id === streamId) {
                onBattleEnded?.(d);
              }
              break;

            case 'live_gift':
              // d may be the gift itself or wrapped — handle both
              if (!d.target_id || d.target_id === streamId) onLiveGift?.(d);
              break;

            case 'live_like':
              if (d.stream_id === streamId) onLiveLike?.(d);
              break;

            case 'live_viewer_joined':
              // Host: a new viewer subscribed → send offer when they're ready
              if (isHost && d.stream_id === streamId) onViewerJoined?.(d.viewer_id);
              break;

            case 'live_viewer_ready':
              // Host: viewer's WebRTC layer is ready → create offer
              if (isHost && d.stream_id === streamId) {
                await sendOfferTo(d.from_user_id);
              }
              break;

            case 'live_viewer_left':
              if (isHost && d.stream_id === streamId) {
                const pc = peersRef.current.get(d.viewer_id);
                if (pc) { try { pc.close(); } catch {} peersRef.current.delete(d.viewer_id); }
                onViewerLeft?.(d.viewer_id);
              }
              break;

            case 'live_offer':
              if (!isHost && d.stream_id === streamId) {
                await handleOffer(d.from_user_id, d.offer);
              }
              break;

            case 'live_answer':
              if (isHost && d.stream_id === streamId) {
                await handleAnswer(d.from_user_id, d.answer);
              }
              break;

            case 'live_ice':
              if (d.stream_id === streamId) {
                await handleIce(d.from_user_id, d.candidate);
              }
              break;

            case 'live_kicked':
              if (d.stream_id === streamId) onKicked?.();
              break;

            case 'live_muted':
              if (d.stream_id === streamId) onMuted?.();
              break;

            case 'live_ended':
              if (d.stream_id === streamId) onEnded?.();
              break;

            default:
              break;
          }
        } catch (e) {
          console.error('Live WS error:', e);
        }
      };

      ws.onerror = (e) => console.error('Live WS error:', e);
      ws.onclose = () => setWsReady(false);
    };

    setup();

    return () => {
      cancelled = true;
      // Unsubscribe
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'live_unsubscribe', stream_id: streamId }));
          wsRef.current.close();
        }
      } catch {}
      wsRef.current = null;
      // Close all peer connections
      peersRef.current.forEach((pc) => { try { pc.close(); } catch {} });
      peersRef.current.clear();
      pendingIceRef.current.clear();
      // Stop local stream
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      setLocalStream(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, userId, isHost, hostId]);

  // Host controls
  const toggleMic = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return false;
    const track = stream.getAudioTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    return !track.enabled; // returns muted state
  }, []);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return false;
    const track = stream.getVideoTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    return !track.enabled; // returns "video off" state
  }, []);

  const flipCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return;
    const currentFacing = videoTrack.getSettings().facingMode || 'user';
    const newFacing = currentFacing === 'user' ? 'environment' : 'user';
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing },
        audio: false,
      });
      const newVideoTrack = newStream.getVideoTracks()[0];
      // Replace track in all peer connections
      peersRef.current.forEach((pc) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) sender.replaceTrack(newVideoTrack).catch(() => {});
      });
      // Update local stream
      stream.removeTrack(videoTrack);
      videoTrack.stop();
      stream.addTrack(newVideoTrack);
      setLocalStream(new MediaStream(stream.getTracks()));
    } catch (e) {
      console.error('Failed to flip camera:', e);
    }
  }, []);

  // === Screen sharing (host only) ===
  const startScreenShare = useCallback(async () => {
    if (!isHost) return false;
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 24 } },
        audio: false,
      });
      const screenTrack = displayStream.getVideoTracks()[0];
      if (!screenTrack) return false;

      const camStream = localStreamRef.current;
      if (!camStream) return false;
      const camVideoTrack = camStream.getVideoTracks()[0];

      // Save original camera track for restore
      cameraStreamRef.current = camVideoTrack;

      // Replace track in all peer connections
      peersRef.current.forEach((pc) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender) sender.replaceTrack(screenTrack).catch(() => {});
      });

      // Update local stream
      if (camVideoTrack) camStream.removeTrack(camVideoTrack);
      camStream.addTrack(screenTrack);
      setLocalStream(new MediaStream(camStream.getTracks()));

      // Auto-stop when user clicks the browser's "Stop sharing" button
      screenTrack.onended = () => {
        stopScreenShare();
      };

      setIsScreenSharing(true);
      return true;
    } catch (e) {
      console.error('Screen share failed:', e);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost]);

  const stopScreenShare = useCallback(async () => {
    const camStream = localStreamRef.current;
    if (!camStream) {
      setIsScreenSharing(false);
      return;
    }
    const screenTrack = camStream.getVideoTracks()[0];
    let camTrack = cameraStreamRef.current;

    try {
      // If saved camera track was stopped, re-capture
      if (!camTrack || camTrack.readyState === 'ended') {
        const newCam = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false,
        });
        camTrack = newCam.getVideoTracks()[0];
      }

      peersRef.current.forEach((pc) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender && camTrack) sender.replaceTrack(camTrack).catch(() => {});
      });

      if (screenTrack) {
        camStream.removeTrack(screenTrack);
        screenTrack.stop();
      }
      if (camTrack) camStream.addTrack(camTrack);
      cameraStreamRef.current = null;
      setLocalStream(new MediaStream(camStream.getTracks()));
    } catch (e) {
      console.error('Stop screen share failed:', e);
    }
    setIsScreenSharing(false);
  }, []);


  return {
    localStream,
    wsReady,
    toggleMic,
    toggleCamera,
    flipCamera,
    startScreenShare,
    stopScreenShare,
    isScreenSharing,
  };
}
